import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { frFR, enUS } from '@mui/material/locale';
import theme from './theme';
import french from './fr.json';

export const LANGUAGE_KEY = 'clutchers-language';
const messagePatterns = Object.keys(french).filter(key => key.includes('{')).map(key => {
  const names = [];
  const pattern = key.split(/(\{\w+\})/g).map(part => {
    if (/^\{\w+\}$/.test(part)) { names.push(part.slice(1, -1)); return '(.+?)'; }
    return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }).join('');
  return { key, names, pattern: new RegExp(`^${pattern}$`, 's') };
});
export function translate(key, values = {}, language = 'fr') {
  // Translate known API messages without altering player names or saved reasons.
  if (language === 'fr' && typeof key === 'string' && !french[key]) {
    for (const item of messagePatterns) {
      const match = key.match(item.pattern);
      if (match) return translate(item.key, Object.fromEntries(item.names.map((name, i) => [name, match[i + 1]])), language);
    }
    for (const suffix of ['The original ban was restored on all servers.', 'Recovery could not be confirmed on every server; check server results before retrying.']) {
      if (key.endsWith(` ${suffix}`)) return `${translate(key.slice(0, -suffix.length - 1), {}, language)} ${french[suffix]}`;
    }
  }
  const message = language === 'fr' ? french[key] || key : key;
  return String(message ?? '').replace(/\{(\w+)\}/g, (match, name) => values[name] ?? match);
}
const defaults = { language: 'fr', locale: 'fr-FR', t: (key, values) => translate(key, values, 'fr'), setLanguage: () => {} };
const LanguageContext = createContext(defaults);
export const useLanguage = () => useContext(LanguageContext);
export function LanguageProvider({ children, initialLanguage }) {
  const [language, setLanguageState] = useState(() => {
    if (initialLanguage === 'en' || initialLanguage === 'fr') return initialLanguage;
    try { return localStorage.getItem(LANGUAGE_KEY) === 'en' ? 'en' : 'fr'; } catch { return 'fr'; }
  });
  const value = useMemo(() => ({ language, locale: language === 'fr' ? 'fr-FR' : 'en-GB',
    t: (key, values) => translate(key, values, language),
    setLanguage: next => { if (next === 'fr' || next === 'en') setLanguageState(next); },
  }), [language]);
  const localizedTheme = useMemo(() => createTheme(theme, language === 'fr' ? frFR : enUS), [language]);
  useEffect(() => {
    document.documentElement.lang = language;
    try { localStorage.setItem(LANGUAGE_KEY, language); } catch { /* Language switching still works when storage is disabled. */ }
  }, [language]);
  return <LanguageContext.Provider value={value}><ThemeProvider theme={localizedTheme}>{children}</ThemeProvider></LanguageContext.Provider>;
}
